import React, { useEffect, useState } from "react";
import { withRouter } from "@/helpers/router";
import { Card, Button, Form, Input } from "antd";
const FormItem = Form.Item;
import DateRangePicker from "@/components/common/date-range-picker";
import FullTextHelp from "@/components/common/full-text-help";
import FormItemLabelWithHelp from "@/components/common/form-item-label-with-help";
import MultiSelect from "@/components/common/multi-select";
import { getDistinctValues } from "@/services/api-upc";
import {
  byKey,
  CASE_PHASES,
  DEFAULT_ACTION_APPLICATION_TYPES,
  FIELDS,
  keepOnlyQueryStrings,
} from "./field-defs";

const FILTERS = [
  { key: "caseNumber" },
  { key: "patentNumber" },
  { key: "casePhase", options: CASE_PHASES },
  { key: "actionApplicationType" },
  { key: "respondent" },
  { key: "claimant" },
  { key: "representatives" },
  { key: "judges" },
  { key: "filingDate", type: "date" }
];

const queryToState = query => {
  const state = {};
  for (const field of FIELDS) {
    if (!field.key || !field.qs) continue;
    state[field.key] = query[field.qs];
  }
  return state;
};

const stateToQuery = state => {
  const query = {};
  for (const field of FIELDS) {
    if (!field.key || !field.qs) continue;
    query[field.qs] = state[field.key];
  }
  return query;
};

const UPCToolbar = ({ location, router }) => {
  const [state, setState] = useState(() => queryToState(keepOnlyQueryStrings(location.query)));

  useEffect(() => {
    setState(queryToState(keepOnlyQueryStrings(location.query)));
  }, [location.query]);

  const applyQuery = nextState => {
    const query = stateToQuery(nextState);
    query.sort = location.query.sort ? location.query.sort : "-filing_date";
    router.push({
      pathname: location.pathname,
      query: {
        ...keepOnlyQueryStrings(query),
        sort: query.sort
      }
    });
  };

  const handleSelectChange = stateKey => value => {
    const next = { ...state, [stateKey]: value };
    setState(next);
    applyQuery(next);
  };

  const onResetClick = () => {
    const cleared = queryToState({ action_application_type: DEFAULT_ACTION_APPLICATION_TYPES });
    setState(cleared);
    router.push({
      pathname: location.pathname,
      query: {
        sort: location.query.sort,
        ...keepOnlyQueryStrings({ action_application_type: DEFAULT_ACTION_APPLICATION_TYPES })
      }
    });
  };

  const renderActionLabel = () => {
    const categoryText = (
      <table>
        <tbody>
          <tr><td><code>Matters before the UPC can fall under one of several action/application types.</code></td></tr>
          <tr><td><code>Applications can be for routine legal filings such as change of counsel.</code></td></tr>
          <tr><td><code>Actions are typically requests for legal determinations such as infringement and validity.</code></td></tr>
        </tbody>
      </table>
    );
    return <FormItemLabelWithHelp label="Action" title="FAQ" content={categoryText} placement="right" />;
  };

  const renderField = filter => {
    const fieldDef = byKey[filter.key];
    const label = filter.key === "actionApplicationType" ? renderActionLabel() : fieldDef.label;
    if (filter.type === "date") {
      return (
        <Form.Item key={filter.key} label={label}>
          <DateRangePicker value={state[filter.key]} onChange={handleSelectChange(filter.key)} allowClear={true} />
        </Form.Item>
      );
    }
    return (
      <Form.Item key={filter.key} label={label}>
        <MultiSelect
          value={state[filter.key]}
          onChange={handleSelectChange(filter.key)}
          optionApiCall={filter.options ? undefined : val => getDistinctValues(fieldDef.field, val)}
          options={filter.options}
          placeholder={fieldDef.placeholder}
        />
      </Form.Item>
    );
  };

  return (
    <Card
      title={<span>{" UPC"}</span>}
      extra={
        <Button size="small" onClick={onResetClick}>
          {"Reset"}
        </Button>
      }
      styles={{ body: { paddingBlock: 12 } }}
    >
      <Form layout="vertical" className="search-toolbar-form">
        <FormItem label={<FullTextHelp />}>
          <Input
            value={state.fullText}
            placeholder={byKey.fullText.placeholder}
            onChange={e => setState(prev => ({ ...prev, fullText: e.target.value }))}
            onPressEnter={() => applyQuery(state)}
          />
        </FormItem>
        {FILTERS.map(filter => renderField(filter))}
      </Form>
    </Card>
  );
};

export default withRouter(UPCToolbar);
